import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AssetDto, GalleryDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  forkJoin,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { GalleryActivityMenuComponent } from '../../../account/menus/gallery-activity-menu/gallery-activity-menu.component';
import { GalleryContextMenuComponent } from '../../../gallery-context-menu/gallery-context-menu.component';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { changeCoverAsset } from '../../../lib/change-cover-asset';
import { Directory } from '../../../lib/get-files-from-event';
import { ForkJoinWithConcurrencyLimit } from '../../../lib/throttled-fork-join';
import { AssetService } from '../../../services/asset.service';
import { DragDropService } from '../../../services/drag-drop.service';
import { GalleryUploadService } from '../../../services/gallery-upload.service';
import { GalleryUrlService } from '../../../services/gallery-url.service';
import { GalleryService } from '../../../services/gallery.service';
import { MoveGalleryService } from '../../../services/move-gallery.service';
import { SectionService } from '../../../services/section.service';
import { ToastService } from '../../../services/toast.service';
import { UploadService } from '../../../services/upload.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { GalleryItemComponent } from '../../../shared-extra/components/gallery-item/gallery-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { DragItemDirective } from '../../../shared/components/drag-drop/drag-item.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SortingButtonComponent } from '../../../shared/components/sorting-button/sorting-button.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { CommonSectionSettingsComponent } from '../common-section-settings/common-section-settings.component';
import { SectionContainerComponent } from '../section-container/section-container.component';

@Component({
  selector: 'app-sub-galleries-section',
  templateUrl: './sub-galleries-section.component.html',
  styleUrls: ['./sub-galleries-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SectionContainerComponent,
    ResizeDirective,
    ContextMenuComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    GalleryItemComponent,
    RouterLink,
    ActivityBadgeComponent,
    GalleryContextMenuComponent,
    LoadingIndicatorComponent,
    GalleryActivityMenuComponent,
    NgTemplateOutlet,
    CommonSectionSettingsComponent,
    AsyncPipe,
    DragItemDirective,
    SortingButtonComponent,
    DropComponent,
  ],
})
export class SubGalleriesSectionComponent {
  private galleryService = inject(GalleryService);
  private galleryUploadService = inject(GalleryUploadService);
  private sectionService = inject(SectionService);
  private assetService = inject(AssetService);
  private swapGalleryInUrlService = inject(GalleryUrlService);
  private dragDropService = inject(DragDropService);
  private moveGalleryService = inject(MoveGalleryService);
  private uploadService = inject(UploadService);
  private toasts = inject(ToastService);
  readonly sectionIndex = input<number>(undefined);
  readonly section = input<Section>(undefined);
  readonly theme = input.required<GalleryTheme>();
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly sections = input<Section[]>(undefined);
  readonly parentGallery = input<GalleryDto>(undefined);
  readonly darkBackground = input<boolean>(undefined);
  readonly addSection = output<number>();

  galleriesEl = viewChild<ElementRef<HTMLDivElement>>('galleriesEl');

  private parentGallery$ = toObservable(this.parentGallery);
  private section$ = toObservable(this.section);
  private sorting$ = this.section$.pipe(
    map(section => section.sorting),
    distinctUntilChanged()
  );

  galleries$ = combineLatest([
    this.parentGallery$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
    this.section$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
  ]).pipe(
    switchMap(([parentGallery, section]) => {
      return this.galleryService.getSectionGalleriesPaginatedAll(section.id, parentGallery.id, this.sorting$);
    }),
    shareReplay(1)
  );
  links$ = combineLatest([
    this.galleries$,
    this.parentGallery$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
  ]).pipe(
    map(([galleries, parentGallery]) => {
      const links = {};
      galleries.forEach(
        gallery => (links[gallery.id] = this.swapGalleryInUrlService.getChildPath(parentGallery, gallery))
      );
      return links;
    })
  );

  private galleries = toSignal(this.galleries$);

  public readonly draggingItem = toSignal(
    this.dragDropService.dragging$.pipe(map(dragging => (dragging?.triggered ? dragging : undefined)))
  );

  public readonly draggingGalleryId = toSignal(
    this.dragDropService.dragging$.pipe(
      map(dragging => {
        if (!dragging) return;
        if (!dragging.triggered) return;
        if (dragging.type !== 'gallery') return;

        if (!dragging.context?.startsWith('sec_')) return;
        return dragging.id;
      })
    )
  );

  readonly dragHovering = toSignal(
    this.dragDropService.hovering$.pipe(
      map(hovering => {
        if (!hovering) return;
        if (hovering.type !== 'gallery') return;
        const section = this.section();
        if (!section) return;
        if (hovering.context !== section.id) return;
        const offsetX = hovering.position.x - hovering.targetBounds.x;
        const dropPosition = offsetX > hovering.targetBounds.width / 2 ? 'after' : 'before';

        return {
          ...hovering,
          dropPosition,
        };
      })
    )
  );

  private galleryDropWatcher = this.dragDropService.dropped$
    .pipe(
      filter(dropped => {
        if (!dropped) return false;
        if (dropped.type !== 'gallery') return false;
        const section = this.section();
        if (!section) return false;
        if (dropped.context === section.id) return true;
        if (dropped.sourceContext === section.id) return true;
        return false;
      }),
      switchMap(dropped => {
        const section = this.section();
        if (!section) return of([dropped, undefined] as const);
        const withinThisSection = dropped.context === dropped.sourceContext;
        const movedIn = !withinThisSection && dropped.context === section.id;
        const galleries = this.galleries();
        if (!movedIn) return of([dropped, galleries.find(g => g.id === dropped.id)] as const);
        return this.galleryService.getGallery(dropped.id).pipe(
          take(1),
          map(gallery => [dropped, gallery] as const)
        );
      }),
      tap(([dropped, gallery]) => {
        const section = this.section();
        const withinThisSection = dropped.context === dropped.sourceContext;
        const movedIn = !withinThisSection && dropped.context === section.id;
        const movedOut = !withinThisSection && dropped.sourceContext === section.id;
        const galleries = this.galleries();

        const offsetX = dropped.position.x - dropped.targetBounds.x;
        const position = offsetX > dropped.targetBounds.width / 2 ? 'after' : 'before';

        if (section.sorting === 'position' || withinThisSection) {
          const newGalleries = galleries.slice();

          if (!movedIn) {
            const currentIndex = newGalleries.findIndex(g => g.id === gallery.id);
            newGalleries.splice(currentIndex, 1);
          }
          const newIndex =
            newGalleries.findIndex(g => g.id === dropped.target) +
            (position === 'after' && dropped.target !== gallery.id ? 1 : 0);

          if (!movedOut) {
            newGalleries.splice(newIndex, 0, gallery);
          }

          newGalleries.forEach((gallery, index) => {
            if (gallery.position === index) return;
            this.galleryService.updateGallery(gallery, {
              position: index,
            });
          });
        } else if (movedIn) {
          this.galleryService
            .getGallery(dropped.id)
            .pipe(take(1))
            .subscribe(droppedGallery => {
              this.galleryService.updateGallery(droppedGallery, {
                section: section.id,
              });
            });
        }
      }),
      tap(([dropped]) => {
        const withinThisSection = dropped.context === dropped.sourceContext;
        if (this.section().sorting === 'position' || !withinThisSection) return;
        this.sectionService.updateSection(this.section(), { sorting: 'position' }).subscribe();
      })
    )
    .subscribe();

  private assetDropWatcher = this.dragDropService.dropped$
    .pipe(
      filter(dropped => {
        if (!dropped) return false;
        if (dropped.type !== 'asset') return false;
        if (dropped.targetType !== 'gallery' && dropped.targetType !== 'new-gallery') return false;
        if (dropped.context !== this.section().id) return false;
        return true;
      }),

      switchMap(dropped => {
        if (dropped.targetType !== 'new-gallery') return of(dropped);
        const galleriesCount = this.galleries().length;
        return this.addGallery().pipe(
          switchMap(gallery =>
            this.galleries$.pipe(
              filter(galleries => galleries.length > galleriesCount),
              take(1),
              map(() => gallery)
            )
          ),
          map(gallery => {
            return {
              ...dropped,
              target: gallery.id,
              targetType: 'gallery',
            };
          })
        );
      }),
      switchMap(dropped => {
        const gallery = this.galleries().find(g => g.id === dropped.target);
        if (!gallery) return of(undefined);
        return forkJoin([
          this.sectionService.getOrCreateSection('asset', gallery.id).pipe(take(1)),
          this.assetService.getAsset(dropped.id, gallery.id).pipe(take(1)),
        ]).pipe(
          switchMap(([section, asset]) => {
            // This should probably be moved to a service, and reused in the move asset dialog
            if (section.sorting !== 'position') {
              return of({ section, gallery, asset, position: null as null });
            }
            return this.assetService
              .getAssetsPaginated(
                section.id,
                gallery.id,
                new BehaviorSubject('-position'),
                new BehaviorSubject(undefined),
                { limit: 1 }
              )
              .pipe(
                take(1),
                map(resp => {
                  const assets = resp.assets;
                  const position = assets.length ? assets[assets.length - 1].position + 1 : 0;
                  return { section, gallery, asset, position };
                })
              );
          })
        );
      }),
      switchMap(d => {
        if (!d) return of(undefined);
        const { section, gallery, asset, position } = d;
        return forkJoin([
          from(
            this.assetService.updateAsset(asset, { section: section.id, gallery: gallery.id, position }, gallery.id)
          ),
          this.updateTeaser(gallery, asset),
        ]);
      })
    )
    .subscribe();

  private galleryUpWatcher = this.dragDropService.dropped$
    .pipe(
      filter(dropped => {
        if (!dropped) return false;
        if (dropped.type !== 'gallery') return false;
        if (dropped.sourceContext !== this.section().id) return false;
        if (dropped.targetType !== 'home') return false;
        return true;
      }),
      tap(dropped => {
        const gallery = this.galleries().find(g => g.id === dropped.id);
        this.moveGalleryService
          .moveGallery(gallery, undefined, this.parentGallery().parent_gallery as string)
          .subscribe();
      })
    )
    .subscribe();

  private updateTeaser(gallery: GalleryDto, asset: AssetDto) {
    if (gallery.teaser?.uuid || !canDisplayAsset(asset)) return of(undefined);
    gallery.teaser = { position: '50% 50%' };
    changeCoverAsset(gallery.teaser, asset);

    return from(
      this.galleryService.updateGallery(gallery, {
        teaser: gallery.teaser,
      })
    );
  }

  readonly dragHoverMoveBeforeGallery = computed(() => {
    const hovering = this.dragHovering();
    const draggingGalleryId = this.draggingGalleryId();
    const galleries = this.galleries();
    if (!draggingGalleryId || !hovering || !galleries) return;
    if (draggingGalleryId === hovering.id) return;

    const index = galleries.findIndex(a => a.id === hovering.id);
    if (hovering.dropPosition === 'after') {
      const galleryAfter = galleries[index + 1];
      if (galleryAfter?.id === draggingGalleryId) return;
      return galleries[index];
    }

    const galleryBefore = galleries[index - 1];
    if (galleryBefore?.id === draggingGalleryId) return;
    return galleryBefore;
  });

  readonly dragHoverMoveAfterGallery = computed(() => {
    const hovering = this.dragHovering();
    const draggingGalleryId = this.draggingGalleryId();
    const galleries = this.galleries();

    if (!draggingGalleryId || !hovering || !galleries) return;
    if (draggingGalleryId === hovering.id) return;

    const index = galleries.findIndex(a => a.id === hovering.id);

    if (hovering.dropPosition === 'before') {
      const galleryAfter = galleries[index - 1];
      if (galleryAfter?.id === draggingGalleryId) return;
      return galleries[index];
    }

    const galleryAfter = galleries[index + 1];
    if (galleryAfter?.id === draggingGalleryId) return;
    return galleryAfter;
  });

  public readonly columnCount = computed(() => {
    const galleries = this.galleries();
    const windowWidth = this.windowWidth();
    const galleriesEl = this.galleriesEl();
    if (!galleriesEl || !galleries) return;
    if (windowWidth < 600) return 1;
    const width = galleriesEl.nativeElement.getBoundingClientRect().width;
    const itemWidth = 370;
    const spacing = 20;
    const columnCount = Math.floor((width + spacing) / (itemWidth + spacing));
    return Math.min(columnCount, galleries.length + 1); // +1 for the "Add Gallery" button
  });

  addingGallery = signal(false);
  sortings = ['position', 'created_at', 'title', 'last_changed_at'] as const;

  readonly dateMode = computed(() => {
    const section = this.section();
    if (!section) return;
    if (section.sorting.endsWith('created_at')) return 'created_at';
    if (section.sorting.endsWith('last_changed_at')) return 'last_changed_at';
  });

  @HostListener('window:resize')
  onResize() {
    this.windowWidth.set(window.innerWidth);
  }
  readonly windowWidth = signal(window.innerWidth);
  readonly isDesktop = computed(() => this.windowWidth() >= 900);

  changeGallerySorting(sorting: string) {
    this.sectionService.updateSection(this.section(), { sorting }).subscribe();
  }

  public async flipSorting() {
    const currentSorting = this.section().sorting;
    if (currentSorting.startsWith('-')) {
      this.changeGallerySorting(currentSorting.substring(1));
    } else {
      this.changeGallerySorting(`-${currentSorting}`);
    }
  }

  addGallery(name?: string) {
    if (this.addingGallery()) return of(undefined);
    this.addingGallery.set(true);
    return from(this.galleryService.createSubGallery(this.parentGallery(), this.section().id, name)).pipe(
      tap(() => {
        this.addingGallery.set(false);
      })
    );
  }

  uploadAssets(items: (File | Directory)[]) {
    if (!this.galleryPermissions().can_edit) return;

    const topLevelFiles = items.filter(item => item instanceof File);
    if (topLevelFiles.length) {
      this.toasts.add(`You can't upload files directly here, please drop a folder`, 'error', { duration: 5000 });
      return;
    }

    const directories = items.filter(item => !(item instanceof File)) as Directory[];
    ForkJoinWithConcurrencyLimit(
      directories.map(directory => of(directory).pipe(switchMap(dir => this.createAndUploadDir(dir)))),
      1
    ).subscribe();
  }

  private createAndUploadDir(directory: Directory) {
    const files = directory.items.filter(item => item instanceof File);
    if (!files.length) {
      this.toasts.add(`Ignoring directory without files: ${directory.name}`, 'info', { duration: 3000 });
      return of(undefined);
    }

    return this.addGallery(directory.name).pipe(
      switchMap(gallery => {
        if (!gallery) return of(undefined);
        return this.sectionService.getOrCreateSection('asset', gallery.id).pipe(
          switchMap(section => {
            return this.galleryUploadService.autoAddTeaserAfterUpload(
              gallery,
              this.uploadService.managedUpload(files, gallery, section.id, [], section.sorting)
            );
          })
        );
      })
    );
  }
}
